import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import moment from 'moment'
import { userSelector } from '@src/selectors/app'
import {
  CoachesCoachingKind,
  EhrAppointmentStateKind,
  SurveysConfigKind,
  UserPropertyOnboardingExperienceKind,
} from '@src/types'
import {
  appointmentRecurrenceSelector,
  coachAssignmentForCoachingKindSelector,
  insurancePolicySelector,
  pastEhrAppointmentsSelector,
} from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { RootStackParamList } from '@src/navigation/types'
import { Storage } from '@src/utils'
import { EhrAppointmentsQuery } from '@src/screens/NutritionistHub/graphql/ehrAppointments.generated'

export const useOnboardingExperienceBanner = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()

  const user = useSelector(userSelector)
  const appointmentRecurrence = useSelector(appointmentRecurrenceSelector)
  const insurancePolicy = useSelector(insurancePolicySelector)
  const pastAppointments = useSelector(pastEhrAppointmentsSelector)
  const primaryCoachingKind =
    user && 'primaryCoachingKind' in user ? user.primaryCoachingKind : undefined
  const coachAssignment = useSelector(
    coachAssignmentForCoachingKindSelector(primaryCoachingKind?.key ?? CoachesCoachingKind.Mnt),
  )

  const firstOccurredAppointment = pastAppointments
    .filter((appointment) => appointment.lastState.kind === EhrAppointmentStateKind.Occurred)
    .sort((a, b) => moment(a.meetingAt).valueOf() - moment(b.meetingAt).valueOf())[0] as
    | EhrAppointmentsQuery['ehrAppointments']['appointments'][0]
    | undefined
  const hasBeenMoreThan24HoursSinceFirstOccurredAppointment = firstOccurredAppointment
    ? moment().diff(moment(firstOccurredAppointment.meetingAt), 'hours') > 24
    : false
  const userIsParticipant = user?.onboardingExperience?.participating
  const weightLossExperience =
    user?.onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss
  // If user had their insurance declined, we don't want to show the banner
  // if they already visited the plan details screen
  const userWithNotEligibleInsuranceVisitPlanDetails =
    insurancePolicy?.declined &&
    Storage.get(Storage.WEIGHT_LOSS_EXPERIENCE_VISIT_PLAN_DETAILS_KEY, false)

  if (
    !coachAssignment?.coach ||
    !userIsParticipant ||
    userWithNotEligibleInsuranceVisitPlanDetails ||
    hasBeenMoreThan24HoursSinceFirstOccurredAppointment
  ) {
    return { shouldShowBanner: false, handleOnPress: () => {} }
  }

  const firstPartSurveyKind = SurveysConfigKind.OnboardingExperienceBasicInformation
  const firstPartSurvey = user.surveyLinks.find(
    (survey) => survey.survey.kind === firstPartSurveyKind,
  )
  // Weight Loss users will ALWAYS see the Pace survey as the second part of the onboarding flow
  // Later on, they will be able to update their plan by answering the Goals surveys
  const secondPartSurveyKind = weightLossExperience
    ? SurveysConfigKind.OnboardingExperiencePace
    : SurveysConfigKind.OnboardingExperienceGoals
  const secondPartSurvey = user.surveyLinks.find(
    (survey) => survey.survey.kind === secondPartSurveyKind,
  )
  const goalsSurvey =
    secondPartSurveyKind === SurveysConfigKind.OnboardingExperienceGoals
      ? secondPartSurvey
      : user.surveyLinks.find(
          (survey) => survey.survey.kind === SurveysConfigKind.OnboardingExperienceGoals,
        )

  const userNotFinishedFirstPartOfTheFlow = !firstPartSurvey?.finished
  const userNotFinishedSecondPartOfTheFlow = !secondPartSurvey?.finished
  // UHC members are not pushed into the recurring appointment step
  const appointmentRecurrenceRequired = !user.uhcMember
  const appointmentRecurrenceFinished = appointmentRecurrence || !appointmentRecurrenceRequired

  // User finished both surveys, but have not scheduled the recurrent appointments
  const userFinishedFlowButHaveNotScheduledRecurrentAppointments =
    firstPartSurvey?.finished &&
    secondPartSurvey?.finished &&
    appointmentRecurrenceRequired &&
    !appointmentRecurrence

  // Weight Loss users that finished the entire onboarding flow
  // They should be able to update their plan by answering the Goals survey
  const weightLossUserFinishedFlowButHaveNotUpdatedTheirPlanYet =
    weightLossExperience &&
    firstPartSurvey?.finished &&
    secondPartSurvey?.finished &&
    appointmentRecurrenceFinished &&
    !goalsSurvey?.finished

  /**
   * We want to show the banner if
   * - User did not finish the first part of the onboarding flow
   *    OR
   * - User finished the first part but not the second part
   *    OR
   * - User finished both parts (surveys) but did not schedule the recurrent appointments (if required)
   *    OR
   * - A Weight Loss user that finished entirely the onboarding flow
   *   (answered all surveys and scheduled the recurrent appointments)
   *   but did not update their plan yet (did not answered the Goals survey)
   */
  const shouldShowBanner =
    userNotFinishedFirstPartOfTheFlow ||
    userNotFinishedSecondPartOfTheFlow ||
    userFinishedFlowButHaveNotScheduledRecurrentAppointments ||
    weightLossUserFinishedFlowButHaveNotUpdatedTheirPlanYet

  const handleOnPress = () => {
    // User Did not start (or finish) the onboarding flow.
    // We should send them to the beginning of the flow
    if (userNotFinishedFirstPartOfTheFlow) {
      navigation.navigate('Questionnaire', {
        questionnaire: firstPartSurveyKind,
      })
      return
    }

    // User finished the first part of the onboarding flow,
    // but have not started the second part yet.
    if (userNotFinishedSecondPartOfTheFlow) {
      navigation.navigate('Questionnaire', {
        questionnaire: secondPartSurveyKind,
      })
      return
    }

    // User finished the onboarding flow, but have not scheduled the recurrent appointments (if required)
    if (!appointmentRecurrenceFinished) {
      navigation.navigate('WeightLossExperiencePersonalPlan')
      return
    }

    // Weight Loss users that can update their plan
    if (weightLossExperience) {
      if (!goalsSurvey?.finished) {
        navigation.navigate('Questionnaire', {
          questionnaire: SurveysConfigKind.OnboardingExperienceGoals,
        })
        return
      }
    }
  }

  return { shouldShowBanner, handleOnPress }
}
